import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { handlingErrorResponse } from "utils/HandlingErrorResponse";
import { RootState } from "store/store";
import { getAuthToken } from "utils/getAuthToken";
import { baseUrl } from "constant";
import {
    LeaveEndpointConst, addGenerateLeaveByCodeUrl, deleteCommentByIdUrl, getApplyForLeaveByIdUrl,
    getEmployeeBalanceDetailsByCodeUrl,
    getEmployeeBalanceTransactionsByCodeUrl,
    getEmployeeLeaveDetailsByIdUrl,
    getLeaveBalanceByTypeCodeUrl,
    getSyncGoogleCalanderLeaveById,
    getTotalContractByCodeUrl,
} from "./const";
import { IAddCommentType, IAllLeaveRequestRequest, IApprovedDeclinedType, IGetCommentRequestType } from "../allLeaveRequest/type";
import {
    IAllLeaveResponseDataType, IDeleteLeaveCommentsRequestType, IGenerateLeaveRequestTypes,
    IGetBalanceByTypeCodeType,
    IGetBalanceDetailType,
    IGetBalanceInfoResponseType,
    IGetCommentResponseType, IGetLeaveDetailsInfoResponseType, IGetSyncGoogleCalanderLeaveResponseType, IGetTotalContractByCodeRequestType, ILeaveTransactionAddRequestType, ILeaveTransactionUpdateRequestType, ILeaveTransactionsResponseType, IRequestType, IResponseType, IResponseWithPaginationType, ISandwichDaysRequestType
} from "./type";
import { ILeaveBalanceData, ILeaveBalanceRequest } from "../leaveBalance/type";
import { IApplyForLeaveType, IGetApplyLeaveResponses, IGetEmployeeResponse } from "../applyForLeave/type";
import { IGenerateLeaveType } from "../generateLeave/type";

export const LeaveApi = createApi({
    reducerPath: 'leaveApi',
    baseQuery: fetchBaseQuery({
        baseUrl: baseUrl,
        prepareHeaders: (headers, { getState }) => {
            const token = getAuthToken(getState() as RootState)
            if (token) {
                headers.set('Authorization', `Bearer ${token}`)
            }
            return headers
        },
    }),
    tagTypes: ['Leave'],
    endpoints: (builder) => ({
        GetLeaveList: builder.mutation<IResponseWithPaginationType<IAllLeaveResponseDataType>, IAllLeaveRequestRequest>({
            query: userReq => ({
                url: LeaveEndpointConst.leaveList,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'ApplyForLeave' }],
        }),
        CheckIsLeaveExists: builder.mutation<IResponseType<any>, any>({
            query: userReq => ({
                url: LeaveEndpointConst.isLeaveExists,
                method: 'POST',
                body: userReq.isLeaveExists
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'CheckIsLeaveExists' }],
        }),
        ApplyForLeave: builder.mutation<IResponseType<string>, IApplyForLeaveType>({
            query: userReq => ({
                url: LeaveEndpointConst.applyForLeave,
                method: 'POST',
                body: userReq.applyForLeave
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'ApplyForLeave' }],
        }),
        GetApplyForLeave: builder.query<IResponseType<IGetApplyLeaveResponses>, IRequestType>({
            query: (leaveParams) => getApplyForLeaveByIdUrl(leaveParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Leave', id: 'ApplyForLeave' }]
        }),
        LeaveApprovedDeclined: builder.mutation<IResponseType<boolean>, IApprovedDeclinedType>({
            query: userReq => ({
                url: LeaveEndpointConst.leaveApprovedDeclined,
                method: 'PUT',
                body: userReq.approvedDeclined
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'LeaveApprovedDeclined' }],
        }),
        GetLeaveComments: builder.mutation<IResponseWithPaginationType<IGetCommentResponseType>, IGetCommentRequestType>({
            query: userReq => ({
                url: LeaveEndpointConst.getCommentList,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'LeaveComments' }],
        }),
        AddLeaveComments: builder.mutation<IResponseType<string>, IAddCommentType>({
            query: userReq => ({
                url: LeaveEndpointConst.addComments,
                method: 'POST',
                body: userReq.commentsObj
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'LeaveComments' }],
        }),
        GetLeaveBalanceList: builder.mutation<IResponseWithPaginationType<ILeaveBalanceData>, ILeaveBalanceRequest>({
            query: userReq => ({
                url: LeaveEndpointConst.leaveBalanceList,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetLeaveBalanceList' }],
        }),
        GetGenerateLeaveEmployee: builder.mutation<IResponseType<IGenerateLeaveType[]>, void>({
            query: () => ({
                url: LeaveEndpointConst.getEmployeeForGenerateLeave,
                method: 'POST',
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetGenerateLeaveEmployee' }],
        }),
        AddGenerateLeave: builder.mutation<IResponseType<number[]>, IGenerateLeaveRequestTypes>({
            query: userReq => ({
                url: addGenerateLeaveByCodeUrl(userReq.params),
                method: 'POST',
                body: userReq.bodyData
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'AddGenerateLeave' }],
        }),
        GetEmployeeList: builder.mutation<IResponseType<IGetEmployeeResponse[]>, void>({
            query: () => ({
                url: LeaveEndpointConst.getEmployeeList,
                method: 'POST',
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetEmployeeList' }],
        }),
        DeleteApplyLeave: builder.mutation<IResponseType<boolean>, IRequestType>({
            query: userReq => ({
                url: LeaveEndpointConst.deleteApplyLeave,
                method: 'DELETE',
                body: [userReq.leaveId]
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'ApplyForLeave' }],
        }),
        DeleteLeaveComment: builder.mutation<IResponseType<boolean>, IDeleteLeaveCommentsRequestType>({
            query: userReq => ({
                url: deleteCommentByIdUrl(userReq),
                method: 'DELETE',
                body: [userReq.commentId]
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'DeleteLeaveComment' }],
        }),
        GetEmployeeLeaveDetailsById: builder.query<IResponseType<IGetLeaveDetailsInfoResponseType>, IRequestType>({
            query: (leaveParams) => getEmployeeLeaveDetailsByIdUrl(leaveParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Leave', id: 'ApplyForLeave' }]
        }),
        GetEmployeeBalanceDetailsByCode: builder.query<IResponseType<IGetBalanceInfoResponseType>, IGetBalanceDetailType>({
            query: (leaveParams) => getEmployeeBalanceDetailsByCodeUrl(leaveParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Leave', id: 'ApplyForLeave' }]
        }),
        GetLeaveBalanceByTypeCodeData: builder.query<IResponseType<number>, IGetBalanceByTypeCodeType>({
            query: (leaveParams) => getLeaveBalanceByTypeCodeUrl(leaveParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Leave', id: 'ApplyForLeave' }]
        }),
        GetTotalContractByCodeData: builder.mutation<IResponseType<number>, IGetTotalContractByCodeRequestType>({
            query: userReq => ({
                url: getTotalContractByCodeUrl(userReq),
                method: 'POST',
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetTotalContractByCodeData' }],
        }),
        GetSandwichDaysData: builder.mutation<IResponseType<number>, ISandwichDaysRequestType>({
            query: userReq => ({
                url: LeaveEndpointConst.getSandwishDaysUrl,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetSandwichDaysData' }],
        }),
        GenerateCalibrateLevae: builder.mutation<IResponseType<string[]>, void>({
            query: () => ({
                url: LeaveEndpointConst.calibrateLeave,
                method: 'POST',
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetLeaveBalanceList' }],
        }),
        GetEmployeeBalanceTransactionsByCode: builder.query<IResponseType<ILeaveTransactionsResponseType>, IGetBalanceDetailType>({
            query: (leaveParams) => getEmployeeBalanceTransactionsByCodeUrl(leaveParams),
            transformErrorResponse: handlingErrorResponse,
            providesTags: [{ type: 'Leave', id: 'GetEmployeeBalanceTransactionsByCode' }]
        }),
        AddTransactionLeave: builder.mutation<IResponseType<ILeaveTransactionsResponseType>, ILeaveTransactionAddRequestType>({
            query: userReq => ({
                url: LeaveEndpointConst.transactionLeave,
                method: 'POST',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetEmployeeBalanceTransactionsByCode' }],
        }),
        TransactionLeave: builder.mutation<IResponseType<ILeaveTransactionsResponseType>, ILeaveTransactionUpdateRequestType>({
            query: userReq => ({
                url: LeaveEndpointConst.transactionLeave,
                method: 'PUT',
                body: userReq
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetEmployeeBalanceTransactionsByCode' }],
        }),
        GetSyncGoogleLeaveDetailsById: builder.mutation<IResponseType<IGetSyncGoogleCalanderLeaveResponseType>, IRequestType>({
            query: (leaveParams) => ({
                url: getSyncGoogleCalanderLeaveById(leaveParams),
                method: 'POST',
            }),
            transformErrorResponse: handlingErrorResponse,
            invalidatesTags: [{ type: 'Leave', id: 'GetSyncGoogleLeaveList' }],
        }),
    }),
})